import { ReactNode, useEffect, useRef, useState } from 'react';
import { Tooltip, TooltipProps } from './tooltip';

interface Props extends TooltipProps {
  tooltipLabel?: ReactNode;
  block?: boolean;
  className?: string;
  tooltipClassName?: string;
}

export function LazyTooltip({
  id,
  children,
  tooltipLabel,
  block,
  hoverable,
  hoverableDelayHide = 250,
  className,
  tooltipClassName,
  ...props
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [withTooltip, setWithTooltip] = useState(false);
  let timer: NodeJS.Timeout | null = null;

  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer);
    }
  };

  const onMouseEnter = () => {
    clearTimer();
    timer = setTimeout(() => {
      setWithTooltip(true);
      if (ref.current) {
        Tooltip.show(ref.current);
      }
    }, 200);
  };

  const onMouseLeave = () => {
    clearTimer();
    if (withTooltip) {
      if (hoverable) {
        timer = setTimeout(() => {
          setWithTooltip(false);
        }, hoverableDelayHide + 1);
      } else {
        setWithTooltip(false);
      }
    }
  };

  useEffect(() => () => clearTimer());

  const body = (
    <>
      {children || tooltipLabel}
      {withTooltip && (
        <Tooltip
          id={id}
          hoverable={hoverable}
          hoverableDelayHide={hoverableDelayHide}
          className={tooltipClassName}
          {...props}
        >
          {tooltipLabel || children}
        </Tooltip>
      )}
    </>
  );

  const wrapperProps = {
    ref,
    className: `LazyTooltip ${className || ''}`,
    onMouseEnter,
    onMouseLeave,
    'data-for': id,
    'data-tip': true,
  };

  if (block) {
    return <div {...wrapperProps}>{body}</div>;
  }

  return <span {...wrapperProps}>{body}</span>;
}
